import React, { useMemo, useRef, useState } from 'react';
import { ProductColor, ProductDetails, VariantDetails, VariantsByKey } from '../../integrations/relewise/types/mapped';
import { ImageWithLoading } from '../../shared/components/image-with-loading';
import { VideoPlaybackHandle, VideoWithLoading } from '../../shared/components/video-with-loading';
import { BaseImageSettings, useImageSettings } from '../../shared/hooks/useImageSettings';
import { ICusNewBuyboxData } from './cus-new-buybox.data';
import { ICusNewBuyboxProps } from './cus-new-buybox.props.autogenerated';
import classnames from 'classnames';
import { IDictionary } from '@msdyn365-commerce/retail-proxy';
import { useMappingService } from '../../integrations/relewise/hooks/serviceHooks';
import { RelewiseMappingService } from '../../integrations/relewise/services/MappingService';
import { Button } from '../../components';
import { CollapsibleGroup } from '../../shared/components/collapsible-group';
import { useScrollLock } from '../../shared/hooks/useScrollLock';
import { useDialogFocus } from '../../shared/hooks/useDialogFocus';
import { useWishlist } from '../../shared/hooks/useWishlist';
import { useAvailabilityByColorAndSize } from '../../shared/hooks/useAvailabilityByColorAndSize';
import { useAddToCart } from '../../shared/hooks/useAddToCart';
import { useProductViewTracker } from '../../integrations/relewise/hooks/useProductViewTracker';
import { useProductMetaInfoData } from './hooks/useProductMetaInfoData';
import { useBuyboxQuerySetter } from './hooks/useBuyboxQuerySetter';
import { useLogBuyboxMissingData } from './hooks/useLogBuyboxMissingData';
import { OverflowTooltip } from '../../shared/components/overflow-tooltip';
import { InfoTooltip } from '../../shared/components/info-tooltip/info-tooltip';
import { HtmlHeadInclude, ImageFetchPriority } from '@msdyn365-commerce/core';
import { useIsMobile } from '../../shared/hooks/useIsMobile';
import { BuyboxHeadMetadata } from './components/buybox-head-metadata';
import { usePdpDatalayer } from '../../integrations/datalayer/hooks/usePdpDatalayer';
import { sanitizeHtmlId } from '../../shared/functions/string-transformers';
import { NEW_BUYBOX_MODULE_NAME } from './cus-new-buybox.const';
import { useLogBuyboxMissingFields } from './hooks/useLogBuyboxMissingFields';

const IMAGE_VIEWPORT_SETTINGS: BaseImageSettings = {
    xs: 'medium',
    sm: 'large',
    md: 'large',
    lg: 'large',
    xl: 'large'
};

// Video thumbnails should always be requested in small size
const VIDEO_THUMBNAIL_VIEWPORT_SETTINGS: BaseImageSettings = {
    xs: 'small',
    sm: 'small',
    md: 'small',
    lg: 'small',
    xl: 'small'
};

const NO_SCROLL_INDEX = -1;

const getDefaultVariant = (
    variantsByKey: VariantsByKey | undefined,
    requestedVariantKey: string | undefined
): VariantDetails | undefined => {
    if (!variantsByKey) {
        return undefined;
    }
    if (requestedVariantKey) {
        return variantsByKey[requestedVariantKey] ?? Object.values(variantsByKey)[0];
    }
    return Object.values(variantsByKey)[0];
};

interface GetInitialSelectionArgs {
    productBuyboxData: ProductDetails | undefined;
    defaultVariant: VariantDetails | undefined;
    defaultColor: ProductColor | undefined;
    query: IDictionary<string> | undefined;
    relewiseMappingService: RelewiseMappingService;
}
const getInitialSelection = ({
    productBuyboxData,
    defaultVariant,
    defaultColor,
    query,
    relewiseMappingService
}: GetInitialSelectionArgs) => {
    if (!productBuyboxData || !productBuyboxData.productItemId) {
        return {
            color: defaultColor,
            size: undefined,
            variant: defaultVariant
        };
    }

    // When url has variant recordId and no queryparams, select that variant
    if (productBuyboxData.requestedVariantKey && !query?.color && !query?.size) {
        return {
            color: defaultColor,
            size: defaultVariant?.size,
            variant: defaultVariant
        };
    }

    const requestedColor = query?.color ? productBuyboxData.allColors?.find(c => c.name === query.color) : undefined;
    const color = requestedColor ?? defaultColor;
    if (!color?.name) {
        return {
            color,
            size: undefined,
            variant: defaultVariant
        };
    }

    const availableSizesForColor = productBuyboxData.compatibleSizesByColor[color.name] ?? [];
    const firstAvailableSize = availableSizesForColor[0];
    let size: string | undefined;
    if (query?.size && availableSizesForColor.includes(query.size)) {
        size = query.size;
    } else if (availableSizesForColor.length === 1) {
        // If there is only one available size, select it
        size = firstAvailableSize;
    }

    const variantKey = relewiseMappingService.mapProductDimensionsToRelewiseVariantId(
        productBuyboxData.productItemId,
        color.name,
        size ?? firstAvailableSize
    );

    return {
        color,
        size,
        variant: productBuyboxData.variantsByKey?.[variantKey] ?? defaultVariant
    };
};

const NewBuybox = (props: ICusNewBuyboxProps<ICusNewBuyboxData>) => {
    const { data, resources, context } = props;
    const gridSettings = context.request.gridSettings;
    const query = context.request.query;
    const productBuyboxData = data.productBuyboxData.result;
    const {
        allColors,
        variantsByKey,
        compatibleSizesByColor,
        productMetaInfo,
        productItemId,
        productRecordId,
        relewiseProductId,
        allSizes,
        brand,
        displayName
    } = productBuyboxData ?? {};

    const imageSettings = useImageSettings(IMAGE_VIEWPORT_SETTINGS);
    const videoThumbnailImageSettings = useImageSettings(VIDEO_THUMBNAIL_VIEWPORT_SETTINGS);
    const relewiseMappingService = useMappingService(context.request, context.telemetry);
    const isMobile = useIsMobile(context);

    const defaultVariant = getDefaultVariant(variantsByKey, productBuyboxData?.requestedVariantKey);
    const defaultColor: ProductColor | undefined = defaultVariant?.color;
    const initialSelection = useMemo(
        () => getInitialSelection({ productBuyboxData, defaultVariant, defaultColor, query, relewiseMappingService }),
        [productBuyboxData, defaultVariant, defaultColor, query, relewiseMappingService]
    );

    const [selectedVariant, setSelectedVariant] = useState(initialSelection.variant);
    const [selectedColor, setSelectedColor] = useState<ProductColor | undefined>(initialSelection.color);
    const [selectedSize, setSelectedSize] = useState<string | undefined>(initialSelection.size);
    const [selectedMediaUrl, setSelectedMediaUrl] = useState<string | undefined>(initialSelection.variant?.media?.[0]?.url);
    const [isSizeModalOpen, setIsSizeModalOpen] = useState(false);
    const [pendingAddToCart, setPendingAddToCart] = useState(false);
    const sizeModalRef = useRef<HTMLDivElement>(null);

    useLogBuyboxMissingData(productBuyboxData, context.telemetry);
    useScrollLock(isSizeModalOpen);
    useDialogFocus(sizeModalRef, isSizeModalOpen);
    useBuyboxQuerySetter(selectedColor, selectedSize);
    useProductViewTracker(relewiseProductId, selectedVariant?.primarySizeVariant?.relewiseVariantId, context.request, context.telemetry);

    useLogBuyboxMissingFields({
        hasLoadedProduct: !!productBuyboxData,
        variantsByKey,
        allColors,
        allSizes,
        displayName,
        selectedVariant,
        telemetry: context.telemetry
    });

    const {
        availabilityByColor,
        currentSelectionOutOfStock,
        availabilityForSelectedColorBySize,
        isLoading: isAvailabilityLoading
    } = useAvailabilityByColorAndSize({
        context,
        variantsByKey,
        allColors,
        selectedVariant,
        selectedColor,
        selectedSize,
        compatibleSizesByColor,
        productItemId,
        moduleName: NEW_BUYBOX_MODULE_NAME
    });

    const {
        handleAddToCartClick,
        addToCartLoading,
        hasError: hasAddToCartError
    } = useAddToCart({
        context,
        selectedVariant,
        productItemId,
        productRecordId,
        unitOfMeasure: productBuyboxData?.unitOfMeasure,
        telemetry: context.telemetry,
        moduleName: NEW_BUYBOX_MODULE_NAME
    });

    const { trackAddToCart } = usePdpDatalayer({
        request: context.request,
        telemetry: context.telemetry,
        productItemId: productItemId,
        selectedVariant,
        selectedColor,
        selectedSize,
        compatibleSizesByColor,
        brand: productBuyboxData?.brand
    });

    const {
        isProductInWishlist,
        toggleWishlist,
        isLoading: isWishlistLoading,
        addToWishlistError,
        isWishlistReady
    } = useWishlist(context, NEW_BUYBOX_MODULE_NAME);
    const isVariantInWishlist = useMemo(
        () => isProductInWishlist(selectedVariant?.primarySizeVariant?.variantRecordId),
        [selectedVariant, isProductInWishlist]
    );

    const metaInfoData = useProductMetaInfoData({ productMetaInfo, resources, productItemId: productItemId });

    const availableSizes = compatibleSizesByColor && selectedColor ? compatibleSizesByColor[selectedColor.name] : [];
    const previewMedia = selectedVariant?.media;

    const isSsr = typeof window === 'undefined';
    const activeMedia = (selectedMediaUrl ? previewMedia?.find(media => media.url === selectedMediaUrl) : undefined) ?? previewMedia?.[0];
    const activeImageUrl = activeMedia?.type === 'Video' ? activeMedia.thumbnail : activeMedia?.url;
    const preloadImageUrl =
        isSsr && activeImageUrl ? `${activeImageUrl}?${isMobile ? imageSettings.xs?.q : imageSettings.lg?.q}` : activeImageUrl;

    const carouselRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const videoRefsByUrl = useRef<Record<string, VideoPlaybackHandle | null>>({});
    const lastScrollIndex = useRef(NO_SCROLL_INDEX);

    const handleCarouselScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const carousel = e.currentTarget;
        const index = Math.round(carousel.scrollLeft / carousel.clientWidth);
        if (index === lastScrollIndex.current || !previewMedia) {
            return;
        }
        lastScrollIndex.current = index;

        const mediaUrl = previewMedia[index]?.url;
        setSelectedMediaUrl(mediaUrl);
        if (mediaUrl) {
            scrollSliderToMedia(mediaUrl);
        }
    };

    const scrollCarouselToIndex = (index: number) => {
        const carousel = carouselRef.current;
        if (!carousel) {
            return;
        }

        carousel.style.scrollBehavior = 'auto';
        carousel.scrollTo({ left: carousel.clientWidth * index });
        carousel.style.scrollBehavior = '';
    };

    const scrollSliderToMedia = (mediaUrl: string) => {
        const slider = sliderRef.current;
        if (!slider || !previewMedia) {
            return;
        }

        const index = previewMedia.findIndex(media => media.url === mediaUrl);
        const thumb = slider.children[index >= 0 ? index : 0] as HTMLElement | undefined;

        if (!thumb) {
            return;
        }

        const isHorizontal = window.matchMedia('(max-width: 920px)').matches;

        if (isHorizontal) {
            slider.scrollTo({
                left: thumb.offsetLeft - slider.clientWidth / 2 + thumb.clientWidth / 2,
                behavior: 'smooth'
            });
        } else {
            slider.scrollTo({
                top: thumb.offsetTop - slider.clientHeight / 2 + thumb.clientHeight / 2,
                behavior: 'smooth'
            });
        }
    };

    const setSelectedVariantFromDimensions = (color: string, size: string): VariantDetails | undefined => {
        if (!productItemId || !variantsByKey) {
            return;
        }
        const variantKey = relewiseMappingService.mapProductDimensionsToRelewiseVariantId(productItemId, color, size);
        const variant = variantsByKey[variantKey];

        if (variant) {
            setSelectedVariant(variant);
        }
        return variant;
    };

    const handlePreviewMediaClick = (mediaUrl: string, index: number) => {
        setSelectedMediaUrl(mediaUrl);
        scrollCarouselToIndex(index);
        scrollSliderToMedia(mediaUrl);
        videoRefsByUrl.current[mediaUrl]?.play();
    };

    const handleSizeSelect = (size: string) => {
        setSelectedSize(size);
        setIsSizeModalOpen(false);

        let variant: VariantDetails | undefined;
        if (selectedColor && size) {
            variant = setSelectedVariantFromDimensions(selectedColor.name, size);
        }

        // Fulfil a deferred add-to-cart (add-to-cart was clicked with no size selected).
        // Use the freshly-computed variant/size so we don't rely on not-yet-applied state.
        if (pendingAddToCart) {
            setPendingAddToCart(false);
            const sizeHasStock = availabilityForSelectedColorBySize ? availabilityForSelectedColorBySize[size] : true;
            if (variant && sizeHasStock) {
                trackAddToCart({ size, variant });
                handleAddToCartClick(variant);
            }
        }
    };

    const closeSizeModal = () => {
        setIsSizeModalOpen(false);
        setPendingAddToCart(false);
    };

    const handleSizeModalKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeSizeModal();
        }
    };

    const handleColorSelect = (color: ProductColor) => {
        if (color.name === selectedColor?.name) {
            return;
        }
        setSelectedColor(color);
        setSelectedMediaUrl(undefined);
        scrollCarouselToIndex(0);
        lastScrollIndex.current = NO_SCROLL_INDEX;

        // If color only has one available size, select it, otherwise, clear size selection
        const availableSizesForColor = compatibleSizesByColor?.[color.name];
        if (availableSizesForColor?.length === 1) {
            setSelectedSize(availableSizesForColor[0]);
        } else {
            setSelectedSize(undefined);
        }

        if (availableSizesForColor && availableSizesForColor.length > 0) {
            setSelectedVariantFromDimensions(color.name, availableSizesForColor[0]);
        }
    };

    const handleWishlistButtonClick = () => {
        toggleWishlist({
            variantRecordId: selectedVariant?.primarySizeVariant?.variantRecordId,
            concatenatedId: selectedVariant?.primarySizeVariant?.concatenatedId,
            relewiseVariantId: selectedVariant?.relewiseVariantId,
            relewiseProductId,
            itemId: productItemId,
            size: selectedVariant?.primarySizeVariant?.size,
            variantDisplayName: selectedVariant?.displayName,
            brand,
            color: selectedColor?.name,
            price: selectedVariant?.finalPrice,
            discount: selectedVariant?.discount
        });
    };

    const handleAddToCartWithTracking = () => {
        // No size chosen yet: open the size selector and defer the add-to-cart
        if (!selectedSize) {
            setPendingAddToCart(true);
            setIsSizeModalOpen(true);
            return;
        }
        trackAddToCart();
        handleAddToCartClick();
    };

    if (!productBuyboxData) {
        return (
            <section className='new-buybox new-buybox--empty' data-testid='buybox-empty-state'>
                <HtmlHeadInclude>
                    <meta name='robots' content='noindex, follow' />
                </HtmlHeadInclude>
                <h1 className='new-buybox__empty-state-title'>{resources.buyboxEmptyStateHeading}</h1>
                <p className='new-buybox__empty-state-body'>{resources.buyboxEmptyStateBody}</p>
            </section>
        );
    }

    return (
        <>
            <BuyboxHeadMetadata
                title={displayName}
                description={productMetaInfo?.description}
                canonicalUrl={context.request.url.requestUrl.origin + context.request.url.requestUrl.pathname}
                imageUrl={activeImageUrl}
                brand={brand}
                allSizes={allSizes}
                productGroupId={productItemId}
                material={productMetaInfo?.composition}
                currencyCode={context.request.channel?.Currency}
                variantsByKey={variantsByKey}
            />
            <HtmlHeadInclude>{preloadImageUrl && <link rel='preload' as='image' href={preloadImageUrl} />}</HtmlHeadInclude>
            <section className='new-buybox' data-variant-record-id={selectedVariant?.variantRecordId}>
                <div className='new-buybox__media-grid'>
                    <div className='new-buybox__media-slider' ref={sliderRef}>
                        {selectedVariant &&
                            previewMedia?.map((media, index) => {
                                const isVideo = media.type === 'Video';
                                const userFriendlyTitle = displayName ? `${displayName} - ${index + 1}` : undefined;
                                const title = userFriendlyTitle || media.altText;
                                return (
                                    <ImageWithLoading
                                        key={`slider-${index}`}
                                        wrapperClassname={classnames('new-buybox__slider-media-wrapper', {
                                            'new-buybox__slider-media-wrapper--active': media.url === activeMedia?.url,
                                            'new-buybox__slider-media-wrapper--video': isVideo
                                        })}
                                        src={isVideo ? media.thumbnail : media.url}
                                        alt={title}
                                        title={title}
                                        imageSettings={{ viewports: isVideo ? videoThumbnailImageSettings : imageSettings }}
                                        gridSettings={gridSettings ?? {}}
                                        loadFailureBehavior='empty'
                                        showEmptyImageThumbnail
                                        onClick={() => handlePreviewMediaClick(media.url, index)}
                                        loading={undefined}
                                        fetchPriority={ImageFetchPriority.Low}
                                        location={NEW_BUYBOX_MODULE_NAME}
                                        telemetry={context.telemetry}
                                        variantConcatenatedId={selectedVariant.primarySizeVariant?.concatenatedId}
                                    />
                                );
                            })}
                    </div>
                    <div className='new-buybox__main-media-container'>
                        <div className='new-buybox__main-carousel' ref={carouselRef} onScroll={handleCarouselScroll}>
                            {previewMedia?.map((media, index) =>
                                media.type === 'Video' ? (
                                    <VideoWithLoading
                                        key={`carousel-${media.url}-${index}`}
                                        videoRef={handle => (videoRefsByUrl.current[media.url] = handle)}
                                        wrapperClassname='new-buybox__main-carousel-item'
                                        src={media.url}
                                        isPriority={index === 0}
                                        isPausable
                                        playLabel={resources.playLabel}
                                        pauseLabel={resources.pauseLabel}
                                        location={NEW_BUYBOX_MODULE_NAME}
                                        telemetry={context.telemetry}
                                        variantConcatenatedId={selectedVariant?.primarySizeVariant?.concatenatedId}
                                    />
                                ) : (
                                    <ImageWithLoading
                                        key={`carousel-${index}`}
                                        wrapperClassname='new-buybox__main-carousel-item'
                                        src={media.url}
                                        alt={displayName || media.altText}
                                        title={displayName || media.altText}
                                        imageSettings={{ viewports: imageSettings }}
                                        gridSettings={gridSettings ?? {}}
                                        loadFailureBehavior='empty'
                                        showEmptyImageThumbnail
                                        loading={index === 0 ? 'eager' : undefined}
                                        fetchPriority={index === 0 ? ImageFetchPriority.High : ImageFetchPriority.Low}
                                        location={NEW_BUYBOX_MODULE_NAME}
                                        telemetry={context.telemetry}
                                        variantConcatenatedId={selectedVariant?.primarySizeVariant?.concatenatedId}
                                    />
                                )
                            )}
                        </div>
                    </div>
                </div>
                <div className='new-buybox__product-info'>
                    <h1 data-testid='buybox-product-title'>{displayName}</h1>
                    {selectedVariant?.badgeLabel && (
                        <OverflowTooltip
                            id='buybox-badge-label'
                            className='new-buybox__label new-buybox__badge-label'
                            isMobile={isMobile}
                            style={{ backgroundColor: selectedVariant.badgeLabel.color, color: selectedVariant.badgeLabel.textColor }}
                        >
                            {selectedVariant.badgeLabel.text.toUpperCase()}
                        </OverflowTooltip>
                    )}
                    <h2 className='new-buybox__price' data-testid='buybox-price-section'>
                        <span
                            className={classnames('new-buybox__final-price', {
                                'new-buybox__final-price--discounted': selectedVariant?.hasDiscount
                            })}
                            data-testid={selectedVariant?.hasDiscount ? 'discounted-price' : 'non-discounted-price'}
                        >
                            {selectedVariant?.hasMemberPrice ? selectedVariant.basePriceFormatted : selectedVariant?.finalPriceFormatted}
                        </span>
                        {selectedVariant?.hasDiscount && (
                            <span className='new-buybox__original-price' data-testid='original-price'>
                                {selectedVariant.basePriceFormatted}
                            </span>
                        )}
                        {!selectedVariant?.hasDiscount && selectedVariant?.inlineLabel && (
                            <OverflowTooltip
                                id='buybox-inline-label'
                                className='new-buybox__label new-buybox__inline-label'
                                isMobile={isMobile}
                                style={{ backgroundColor: selectedVariant.inlineLabel.color, color: selectedVariant.inlineLabel.textColor }}
                            >
                                {selectedVariant.inlineLabel.text.toUpperCase()}
                            </OverflowTooltip>
                        )}
                    </h2>
                    <div id='color-options-label' className='new-buybox__dimension-label'>
                        {resources.productDimensionTypeColor}
                    </div>
                    <ul className='new-buybox__color-options' aria-labelledby='color-options-label' role='radiogroup'>
                        {allColors?.map(color => {
                            const doesColorHaveStock = availabilityByColor ? availabilityByColor[color.name] : true;
                            const tooltipId = sanitizeHtmlId(`buybox-color-${color.name}`);
                            return (
                                <li key={`${color.name}-availability-${doesColorHaveStock}`}>
                                    <InfoTooltip id={tooltipId} isMobile={isMobile} content={color.name}>
                                        <button
                                            type='button'
                                            className={classnames('new-buybox__color-item', {
                                                'new-buybox__color-item--selected': selectedColor?.name === color.name,
                                                'new-buybox__color-item--out-of-stock': !doesColorHaveStock
                                            })}
                                            style={{ '--color-hex': color.hex ?? 'transparent' } as React.CSSProperties}
                                            onClick={() => handleColorSelect(color)}
                                            role='radio'
                                            aria-checked={selectedColor?.name === color.name}
                                            aria-label={color.name}
                                        />
                                    </InfoTooltip>
                                </li>
                            );
                        })}
                    </ul>
                    <div className='new-buybox__selected-color-name' data-testid='buybox-color-name'>{`${
                        selectedColor?.colorRefinerGroup ? `${selectedColor.colorRefinerGroup} / ` : ''
                    }${selectedColor?.name}`}</div>
                    <div id='size-button-label' className='new-buybox__dimension-label'>
                        {resources.productDimensionTypeSize}
                    </div>
                    <button
                        className={classnames('new-buybox__size-trigger', {
                            'new-buybox__size-trigger--open': isSizeModalOpen
                        })}
                        onClick={() => setIsSizeModalOpen(true)}
                        aria-labelledby='size-button-label'
                        aria-haspopup='dialog'
                        aria-expanded={isSizeModalOpen}
                        data-testid='size-select-trigger'
                        disabled={isAvailabilityLoading}
                    >
                        <span data-testid='selected-size-option'>{selectedSize ?? resources.sizeSelectionHeading}</span>
                    </button>
                    <div
                        className={classnames('new-buybox__out-of-stock-message', {
                            'new-buybox__out-of-stock-message--visible': currentSelectionOutOfStock
                        })}
                        role='alert'
                    >
                        {resources.outOfStockText}
                    </div>

                    {isSizeModalOpen && (
                        <div className='new-buybox__size-modal-overlay' onClick={closeSizeModal}>
                            <div
                                className='new-buybox__size-modal'
                                role='dialog'
                                aria-modal='true'
                                aria-labelledby='size-modal-title'
                                tabIndex={-1}
                                ref={sizeModalRef}
                                onClick={e => e.stopPropagation()}
                                onKeyDown={handleSizeModalKeyDown}
                            >
                                <div className='new-buybox__size-modal-header'>
                                    <span id='size-modal-title' className='new-buybox__size-modal-title'>
                                        {resources.productDimensionTypeSize}
                                    </span>
                                    <button
                                        className='new-buybox__size-modal-close'
                                        onClick={closeSizeModal}
                                        data-testid='size-modal-close-button'
                                        aria-label={resources.closeButtonLabel}
                                        type='button'
                                    >
                                        &times;
                                    </button>
                                </div>
                                <ul
                                    className='new-buybox__size-options'
                                    data-testid='size-select-options'
                                    role='radiogroup'
                                    aria-labelledby='size-modal-title'
                                >
                                    {availableSizes?.map(size => {
                                        const doesSizeHaveStock = availabilityForSelectedColorBySize
                                            ? availabilityForSelectedColorBySize[size]
                                            : true;
                                        return (
                                            <li key={size}>
                                                <button
                                                    type='button'
                                                    className={classnames('new-buybox__size-item', {
                                                        'new-buybox__size-item--selected': selectedSize === size,
                                                        'new-buybox__size-item--out-of-stock': !doesSizeHaveStock
                                                    })}
                                                    onClick={() => handleSizeSelect(size)}
                                                    role='radio'
                                                    aria-checked={selectedSize === size}
                                                    data-testid='size-option'
                                                >
                                                    {size}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    )}

                    <Button
                        className='new-buybox__add-to-cart-button'
                        disabled={!selectedColor || currentSelectionOutOfStock || isAvailabilityLoading}
                        onClick={handleAddToCartWithTracking}
                        isLoading={addToCartLoading}
                        data-testid='add-to-cart-button'
                    >
                        {resources.addToCartText}
                    </Button>
                    {hasAddToCartError && (
                        <div className='new-buybox__add-to-cart-error-message'>{resources.addedToCartFailureMessage}</div>
                    )}
                    <Button
                        className={classnames('new-buybox__add-to-wishlist-button', {
                            'new-buybox__add-to-wishlist-button--in-wishlist': isVariantInWishlist
                        })}
                        variant='secondary'
                        iconName={isVariantInWishlist ? 'heart-filled' : 'heart'}
                        onClick={handleWishlistButtonClick}
                        isLoading={isWishlistLoading}
                        data-testid='add-to-wishlist-button'
                        data-wishlist-state={isVariantInWishlist ? 'active' : 'inactive'}
                        disabled={!isWishlistReady}
                    >
                        {isVariantInWishlist ? resources.removeFromWishlistButtonText : resources.addToWishlistButtonText}
                    </Button>
                    {addToWishlistError && (
                        <div className='new-buybox__add-to-wishlist-error-message'>{resources.addItemToWishlistError}</div>
                    )}

                    <CollapsibleGroup className='new-buybox__product-meta-info-section' items={metaInfoData} />
                </div>
            </section>
        </>
    );
};

export default NewBuybox;
